import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, defer, of } from 'rxjs';
import { tap, switchMap, catchError, shareReplay } from 'rxjs/operators';
import { loadScript } from 'src/app/core/helpers/script-loader/script-loader.helper';
import { environment } from 'src/environments/environment';

export interface SDKAnalyticsInstance {
  track(payload: unknown): unknown;
  setUserProperties(properties: unknown): unknown;
  setDefaultParameters(parameters: unknown): unknown;
  [key: string]: (...args: unknown[]) => unknown;
}

type SDKAnalyticsFactory = (config: GA4SdkConfig) => SDKAnalyticsInstance;

interface GA4SdkConfig {
  readonly webview: boolean;
  readonly allowlist?: boolean;
  readonly use_sessionStorage?: boolean;
  readonly dual_tagging?: boolean;
  readonly gtm?: string;
}

@Injectable({ providedIn: 'root' })
export class ObservabilidadeInitializeService {
  private sdkInstance: SDKAnalyticsInstance | undefined = undefined;
  private provedorCarregado = false;
  private observavelCarregamento$: Observable<void> | null = null;
  private readonly readySubject = new ReplaySubject<void>(1);

  inicializar(): Observable<void> {
    if (this.provedorCarregado && this.sdkInstance) {
      this.readySubject.next();
      return of(undefined);
    }

    if (this.observavelCarregamento$) {
      return this.observavelCarregamento$;
    }

    this.observavelCarregamento$ = this.carregarProvedor$().pipe(
      tap(() => this.readySubject.next()),
      switchMap(() => of(undefined)),
      catchError((error) => {
        console.error('[ObservabilidadeInitializeService] Falha ao carregar SDK', error);
        return of(undefined);
      }),
      shareReplay(1)
    );

    return this.observavelCarregamento$;
  }

  get instance(): SDKAnalyticsInstance | undefined {
    return this.sdkInstance;
  }

  get estaCarregado(): boolean {
    return this.provedorCarregado && this.sdkInstance !== undefined;
  }

  private carregarProvedor$(): Observable<void> {
    const cdnUrl = environment.ga4CdnUrl;
    return defer(() => loadScript(cdnUrl, 'SDKAnalytics')).pipe(
      tap((loaded) => {
        const factory = loaded as SDKAnalyticsFactory;
        const config: GA4SdkConfig = {
          gtm: environment.gtm,
          webview: false,
          allowlist: true,
          use_sessionStorage: false,
          dual_tagging: false
        };
        const sdk = factory(config);
        this.sdkInstance = sdk;
        this.provedorCarregado = true;
      }),
      switchMap(() => of(undefined))
    );
  }
}
